import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Badge, Modal } from '../../../components/ui';
import {
  DollarSign, Trophy, TrendingUp, Percent,
  AlertCircle, RefreshCw, Eye, Search, Calendar,
} from 'lucide-react';
import { diaOperativo } from '../../../utils/date';
import { fetchAPI } from '../../../services/apiClient';
import { useAuth } from '../../../app/auth';
import {
  fetchReportePorSorteo,
  fetchLocalesReporte,
  fetchListaConsolidada,
  fetchTicketsSorteo,
  type ReporteResponse,
  type ReporteSorteo,
  type LocalOption,
  type TicketsSorteoResponse,
} from '../services/consultasService';
import { NumerosGrid, type TipoNumerosGrid } from '../../../components/NumerosGrid';
import { DynamicListTable, type DynamicColumn } from '../../../components/DynamicListTable';

// ─── Utilidades ────────────────────────────────────────

function formatMoney(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return '\u20A10';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '\u20A10';
  return `\u20A1${n.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const monto = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const mapMontos = (rows: any[], keys: string[]): any[] =>
  rows.map((row, index) => {
    const out = { ...row, id: row.id ?? index + 1 };
    for (const key of keys) {
      if (key in out) out[key] = monto(out[key]);
    }
    return out;
  });

function buildDayTabs(): { label: string; iso: string; sub: string }[] {
  const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  return Array.from({ length: 8 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = d.toLocaleDateString('sv-SE'); // sv-SE da YYYY-MM-DD en hora local (evita bug UTC que desplaza la fecha 1 día)
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return {
      label: i === 0 ? 'Hoy' : i === 1 ? 'Mañana' : dias[d.getDay()],
      iso,
      sub: `${dd}/${mm}`,
    };
  });
}

// ─── Columnas para DynamicListTable (misma lógica que ListasPage) ───

const columnsByTipo = (tipo: string): DynamicColumn[] => {
  switch (tipo) {
    case 'NORMAL':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'REVENTADO':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '120px' },
        { key: 'vendido_rev1', label: 'REV1', type: 'readonly', width: '160px' },
        { key: 'vendido_rev2', label: 'REV2', type: 'readonly', width: '160px' },
      ];
    case 'MEGA':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '100px' },
        { key: 'rev1', label: 'REV1', type: 'readonly', width: '130px' },
        { key: 'rev2', label: 'REV2', type: 'readonly', width: '130px' },
        { key: 'num2', label: 'NÚM2', type: 'readonly', width: '80px' },
        { key: 'monto_mega', label: 'MONTO', type: 'readonly', width: '140px' },
      ];
    case 'MONAZOS':
      return [
        { key: 'numero', label: 'NUM', type: 'readonly', width: '120px' },
        { key: 'tipo', label: 'TIPO', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'FECHAS':
      return [
        { key: 'numero', label: 'FECHA', type: 'readonly', width: '100px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'PARLEY':
      return [
        { key: 'num1', label: 'NUM1', type: 'readonly', width: '120px' },
        { key: 'num2', label: 'NUM2', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    default:
      return [];
  }
};

const montoKeysByTipo: Record<string, string[]> = {
  NORMAL: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  REVENTADO: ['vendido_rev1', 'vendido_rev2'],
  MEGA: ['rev1', 'rev2', 'monto_mega'],
  MONAZOS: ['vendido'],
  FECHAS: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  PARLEY: ['vendido'],
};

// ─── Modal de detalle: Lista + Tickets ─────────────────

const REFRESH_INTERVAL = 30_000;

const DetalleModal: React.FC<{
  sorteo: ReporteSorteo;
  fecha: string;
  localId?: string;
  onClose: () => void;
}> = ({ sorteo, fecha, localId, onClose }) => {
  const [tab, setTab] = useState<'lista' | 'tickets'>('lista');
  const [listaRows, setListaRows] = useState<any[]>([]);
  const [ticketsData, setTicketsData] = useState<TicketsSorteoResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Cargar lista consolidada (misma lógica que ListasPage)
  useEffect(() => {
    if (tab !== 'lista') return;
    setLoading(true);
    fetchListaConsolidada(sorteo.sorteo_id, fecha, localId)
      .then((data) => {
        const tipo = sorteo.tipo_id;
        if (tipo === 'NORMAL') {
          setListaRows((data || []).map((r: any) => ({
            numero: String(r.numero).padStart(2, '0'),
            vendido: Number(r.monto_total) || 0,
          })));
        } else {
          setListaRows(data || []);
        }
      })
      .catch(() => setListaRows([]))
      .finally(() => setLoading(false));
  }, [tab, sorteo.sorteo_id, fecha, localId, sorteo.tipo_id]);

  // Cargar tickets
  useEffect(() => {
    if (tab !== 'tickets') return;
    setLoading(true);
    fetchTicketsSorteo(sorteo.sorteo_id, fecha, localId)
      .then(setTicketsData)
      .catch(() => setTicketsData(null))
      .finally(() => setLoading(false));
  }, [tab, sorteo.sorteo_id, fecha, localId]);

  const tipo = sorteo.tipo_id;
  const isGridType = tipo === 'NORMAL' || tipo === 'REVENTADO';

  // Para DynamicListTable: formato moneda
  const formattedRows = useMemo(() => {
    if (tipo === 'REVENTADO') return listaRows;
    return mapMontos(listaRows, montoKeysByTipo[tipo] || ['vendido']);
  }, [listaRows, tipo]);

  return (
    <Modal isOpen onClose={onClose} title={sorteo.sorteo_nombre} size="xl">
      <div className="space-y-4">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          <button
            onClick={() => setTab('lista')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'lista'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Lista
          </button>
          <button
            onClick={() => setTab('tickets')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'tickets'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Tickets
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400">
            <RefreshCw size={24} className="mx-auto mb-2 animate-spin" />
            <p className="text-sm">Cargando...</p>
          </div>
        ) : tab === 'lista' ? (
          /* ─── Vista Lista (idéntica a ListasPage) ─── */
          listaRows.length > 0 ? (
            <div className="overflow-x-auto">
              {isGridType ? (
                <NumerosGrid
                  rows={listaRows}
                  tipo={tipo as TipoNumerosGrid}
                  hidePrintButton
                />
              ) : (
                <DynamicListTable
                  columns={columnsByTipo(tipo)}
                  rows={formattedRows}
                  onSave={async () => {}}
                  idField="id"
                />
              )}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">No hay números anotados para este sorteo</p>
          )
        ) : tab === 'tickets' && ticketsData ? (
          /* ─── Vista Tickets ─── */
          <>
            <div className="text-sm text-gray-500 flex justify-between">
              <span>{ticketsData.tickets.length} tickets</span>
              <span className="font-bold text-gray-900">Total: {formatMoney(ticketsData.total)}</span>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {ticketsData.tickets.map((t, i) => {
                const esFechaDistinta = t.fecha_operativa && t.fecha_operativa !== fecha;
                return (
                  <div key={i} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-sm font-bold text-gray-800">{t.ticket_number}</span>
                      <span className="text-sm font-bold">{formatMoney(t.monto)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{t.local_nombre}</span>
                      {t.created_at && (
                        <span>{new Date(t.created_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                      {esFechaDistinta && (
                        <Badge variant="warning" size="sm">
                          Fecha: {t.fecha_operativa!.split('-').reverse().join('/')}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {t.jugadas.map((j, ji) => (
                        <span key={ji} className="inline-block bg-gray-100 text-gray-700 text-xs font-mono px-2 py-0.5 rounded">
                          {j.num1}{j.num2 ? `-${j.num2}` : ''} {formatMoney(j.monto)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {ticketsData.tickets.length === 0 && (
                <p className="text-center text-gray-400 py-6">No hay tickets</p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

// ─── Pagina principal de consultas ──────────────────────────────

export const ConsultasPage: React.FC = () => {
  const { user } = useAuth();
  const esSupervisor = user?.role === 'SUPERVISOR';
  const puedeVerTotales = user?.role === 'BANCA' || user?.role === 'SUPERVISOR';
  const dayTabs = buildDayTabs();
  const [fecha, setFecha] = useState(diaOperativo());
  const [localId, setLocalId] = useState('');
  const [reporte, setReporte] = useState<ReporteResponse | null>(null);
  const [locales, setLocales] = useState<LocalOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [detalleSorteo, setDetalleSorteo] = useState<ReporteSorteo | null>(null);
  const [fechaManual, setFechaManual] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [diasConFuturas, setDiasConFuturas] = useState<Set<string>>(new Set());
  const [hoyTieneFuturas, setHoyTieneFuturas] = useState(false);
  const [modoPremios, setModoPremios] = useState<'cambiados' | 'totales'>('cambiados');

  useEffect(() => {
    fetchLocalesReporte().then(setLocales).catch(() => {});
    fetchAPI<{ fechas: string[]; hoy_tiene_futuras: boolean }>('/consultas/dias-con-futuras')
      .then((d) => {
        setDiasConFuturas(new Set(d.fechas));
        setHoyTieneFuturas(d.hoy_tiene_futuras);
      })
      .catch(() => {});
  }, []);

  const cargarReporte = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Solo se propaga modoPremios cuando el usuario tiene permiso y eligió "totales".
      // En cualquier otro caso, llamada idéntica al histórico (sin query "modo_premios").
      const modoParam = puedeVerTotales && modoPremios === 'totales' ? 'totales' : undefined;
      const data = await fetchReportePorSorteo(fecha, undefined, localId || undefined, undefined, modoParam);
      setReporte(data);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err.message || 'Error al cargar consulta');
    } finally {
      setLoading(false);
    }
  }, [fecha, localId, modoPremios, puedeVerTotales]);

  useEffect(() => { cargarReporte(); }, [cargarReporte]);

  useEffect(() => {
    const interval = setInterval(cargarReporte, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [cargarReporte]);

  const totales = reporte?.totales;
  const sorteos = reporte?.sorteos || [];
  const isTabDate = dayTabs.some(t => t.iso === fecha);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultas</h1>
          <p className="text-sm text-gray-500">Estado del día por local y sorteo</p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-gray-400">
              {lastUpdate.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={cargarReporte}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Day tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {dayTabs.map((tab, i) => {
          const hasDot = diasConFuturas.has(tab.iso) || (i === 0 && hoyTieneFuturas);
          return (
          <button
            key={tab.iso}
            onClick={() => { setFecha(tab.iso); setShowDatePicker(false); }}
            className={`relative flex flex-col items-center min-w-[64px] px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
              fecha === tab.iso && !showDatePicker
                ? 'bg-blue-600 border-blue-500 text-white shadow-md'
                : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
            }`}
          >
            <span className="text-xs font-semibold">{tab.label}</span>
            <span className={`text-[11px] ${fecha === tab.iso && !showDatePicker ? 'text-blue-100' : 'text-gray-400'}`}>{tab.sub}</span>
            {hasDot && (
              <span className="absolute top-1 right-1.5 w-2 h-2 rounded-full bg-red-500" title="Tiene ventas a futuro" />
            )}
          </button>
          );
        })}
        <button
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={`flex items-center gap-1 min-w-[64px] px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
            showDatePicker || (!isTabDate && fecha !== diaOperativo())
              ? 'bg-blue-600 border-blue-500 text-white shadow-md'
              : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
          }`}
        >
          <Calendar size={14} />
          <span className="text-xs">Otra</span>
        </button>
      </div>

      {showDatePicker && (
        <Card>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Fecha:</label>
            <input
              type="date"
              value={fechaManual || fecha}
              onChange={(e) => { setFechaManual(e.target.value); setFecha(e.target.value); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </Card>
      )}

      {/* Filtro: Local */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
            >
              <option value="">Todos los locales</option>
              {locales.map((l) => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Toggle modo de premios — solo BANCA / SUPERVISOR */}
      {puedeVerTotales && (
        <Card>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Premios:</label>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group">
                <button
                  type="button"
                  onClick={() => setModoPremios('cambiados')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    modoPremios === 'cambiados'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cambiados
                </button>
                <button
                  type="button"
                  onClick={() => setModoPremios('totales')}
                  className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 transition-colors ${
                    modoPremios === 'totales'
                      ? 'bg-amber-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Totales
                </button>
              </div>
              <Badge variant={modoPremios === 'totales' ? 'warning' : 'primary'} size="sm">
                Modo: {modoPremios === 'totales' ? 'Premios totales' : 'Premios cambiados'}
              </Badge>
            </div>
          </div>
        </Card>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Tabla de sorteos */}
      {sorteos.length > 0 ? (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Sorteo</th>
                  <th className="text-center py-3 px-3 font-semibold text-gray-600 hidden sm:table-cell">Estado</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-600">Ventas</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-600 hidden sm:table-cell">Premios</th>
                  {!esSupervisor && <th className="text-right py-3 px-4 font-semibold text-gray-600 hidden md:table-cell">Comisión banca</th>}
                  <th className="text-right py-3 px-4 font-semibold text-gray-600">Balance</th>
                  <th className="text-center py-3 px-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorteos.map((s) => {
                  const isCerrado = s.estado === 'CERRADO';
                  const isAbierto = s.estado === 'ABIERTO';
                  const ventas = parseFloat(s.total_vendido);
                  const premiosCambiados = parseFloat(s.total_premios_pagados);
                  const comision = parseFloat(s.comision_ganada);
                  // En modo "totales" la columna Premios muestra el total generado del sorteo.
                  const premiosMostrado = modoPremios === 'totales' && s.premios_totales !== undefined
                    ? parseFloat(s.premios_totales)
                    : premiosCambiados;
                  // Balance sigue al modo activo: usa el mismo valor que se muestra en Premios.
                  const balance = ventas - premiosMostrado - comision;
                  if (ventas === 0 && premiosCambiados === 0 && !(modoPremios === 'totales' && premiosMostrado > 0)) return null;
                  return (
                    <tr
                      key={s.sorteo_id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setDetalleSorteo(s)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                            isAbierto ? 'bg-green-500' : isCerrado ? 'bg-red-500' : 'bg-gray-400'
                          }`} />
                          <span className={`font-medium ${
                            isAbierto ? 'text-green-700' : isCerrado ? 'text-red-700' : 'text-gray-600'
                          }`}>
                            {s.sorteo_nombre}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center hidden sm:table-cell">
                        <Badge
                          variant={isAbierto ? 'success' : isCerrado ? 'danger' : 'neutral'}
                          size="sm"
                        >
                          {isAbierto ? 'Abierto' : isCerrado ? 'Cerrado' : s.estado}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right font-bold">{formatMoney(ventas)}</td>
                      <td className={`py-3 px-4 text-right font-medium hidden sm:table-cell ${modoPremios === 'totales' ? 'text-amber-700' : 'text-yellow-600'}`}>
                        {premiosMostrado != null && !Number.isNaN(premiosMostrado)
                          ? formatMoney(premiosMostrado)
                          : <span className="text-gray-300">—</span>}
                      </td>
                      {!esSupervisor && <td className="py-3 px-4 text-right font-medium text-blue-600 hidden md:table-cell">{formatMoney(comision)}</td>}
                      <td className={`py-3 px-4 text-right font-bold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatMoney(balance)}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <Eye size={16} className="text-gray-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {(() => {
                  const totalPremiosMostrado = modoPremios === 'totales' && totales?.total_premios_totales !== undefined
                    ? parseFloat(totales.total_premios_totales)
                    : parseFloat(totales?.total_premios_pagados ?? 'NaN');
                  // Balance total también sigue al modo activo.
                  // En modo cambiados se respeta el `total_pagas` precalculado por el backend
                  // (que ya descuenta premios cambiados + comisión). En modo totales se
                  // recalcula localmente: ventas - premios_totales - comisión.
                  const totalBalance = modoPremios === 'totales' && !Number.isNaN(totalPremiosMostrado)
                    ? parseFloat(totales?.total_vendido ?? '0') - totalPremiosMostrado - parseFloat(totales?.total_comision_ganada ?? '0')
                    : parseFloat(totales?.total_pagas ?? '0');
                  return (
                <tr className="bg-gray-50 border-t-2 border-gray-300 font-bold">
                  <td className="py-3 px-4 text-gray-700" colSpan={2}>
                    Total
                  </td>
                  <td className="py-3 px-4 text-right text-lg">{formatMoney(totales?.total_vendido)}</td>
                  <td className={`py-3 px-4 text-right hidden sm:table-cell ${modoPremios === 'totales' ? 'text-amber-700' : 'text-yellow-600'}`}>
                    {totalPremiosMostrado != null && !Number.isNaN(totalPremiosMostrado)
                      ? formatMoney(totalPremiosMostrado)
                      : <span className="text-gray-300">—</span>}
                  </td>
                  {!esSupervisor && <td className="py-3 px-4 text-right text-blue-600 hidden md:table-cell">{formatMoney(totales?.total_comision_ganada)}</td>}
                  <td className={`py-3 px-4 text-right text-lg ${totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatMoney(totalBalance)}
                  </td>
                  <td className="py-3 px-3"></td>
                </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </Card>
      ) : reporte && !loading ? (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <Search size={48} className="mx-auto mb-3" />
            <p className="text-lg font-medium">No hay ventas para esta consulta</p>
            <p className="text-sm mt-1">Pruebe con otra fecha o local</p>
          </div>
        </Card>
      ) : null}

      {/* Totales en tarjetas */}
      {totales && sorteos.some(s => parseFloat(s.total_vendido) > 0 || parseFloat(s.total_premios_pagados) > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <div className="text-center">
              <DollarSign className="mx-auto text-blue-600 mb-1" size={20} />
              <p className="text-xs text-gray-500">Ventas</p>
              <p className="text-lg font-bold">{formatMoney(totales.total_vendido)}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <Trophy className="mx-auto text-yellow-600 mb-1" size={20} />
              <p className="text-xs text-gray-500">Premios</p>
              <p className="text-lg font-bold text-yellow-600">{formatMoney(totales.total_premios_pagados)}</p>
            </div>
          </Card>
          {modoPremios === 'totales' && totales.total_premios_totales !== undefined && (
            <Card>
              <div className="text-center">
                <Trophy className="mx-auto text-amber-600 mb-1" size={20} />
                <p className="text-xs text-gray-500" title="Premios generados por el sorteo (incluye no cobrados). Solo informativo.">
                  Premios totales
                </p>
                <p className="text-lg font-bold text-amber-700">{formatMoney(totales.total_premios_totales)}</p>
              </div>
            </Card>
          )}
          {!esSupervisor && (
          <Card>
            <div className="text-center">
              <Percent className="mx-auto text-blue-600 mb-1" size={20} />
              <p className="text-xs text-gray-500">Comisión banca</p>
              <p className="text-lg font-bold text-blue-600">{formatMoney(totales.total_comision_ganada)}</p>
            </div>
          </Card>
          )}
          <Card>
            <div className="text-center">
              <TrendingUp className="mx-auto text-green-600 mb-1" size={20} />
              <p className="text-xs text-gray-500">Balance est.</p>
              <p className={`text-lg font-bold ${parseFloat(totales.total_pagas) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatMoney(totales.total_pagas)}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Loading */}
      {loading && !reporte && (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <RefreshCw size={32} className="mx-auto mb-3 animate-spin" />
            <p>Cargando consulta...</p>
          </div>
        </Card>
      )}

      {/* Nota informativa */}
      {reporte && sorteos.length > 0 && (
        <p className="text-xs text-gray-400 text-center">
          Premios muestra únicamente premios efectivamente cambiados (pagados). Balance = Ventas − Premios − Comisión.
        </p>
      )}

      {/* Modal de detalle */}
      {detalleSorteo && (
        <DetalleModal
          sorteo={detalleSorteo}
          fecha={fecha}
          localId={localId || undefined}
          onClose={() => setDetalleSorteo(null)}
        />
      )}
    </div>
  );
};

export default ConsultasPage;
